import {
	createLocalDataSource,
	useSortHandler
} from "client/Hooks";
import {
	ReactElement,
	RefObject,
	useEffect,
	useRef,
	useState
} from "react";
import {
	Pagination,
	TableView,
	ConfirmationDialog,
	IconButton
} from "client/Components";
import { contains } from "client/Data/searching";
import { localize } from "client_legacy/ndaw/culture";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { UserAndUserGroupKind } from "./UserAndUserGroupCollection";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { UserGroupOverviewModel } from "client/Api/UserGroupsManagement/userGroupsManagement";

const {
	Row,
	Cell
} = TableView;

export type UserGroupCollectionMode = {
	kind: UserAndUserGroupKind.Read;
} | {
	kind: UserAndUserGroupKind.Edit;
	onChange(value: readonly number[]): void;
};

export type UserGroupCollectionProps = {
	allData: readonly UserGroupOverviewModel[];
	value: readonly number[];
	searchTerm: string;
	mode: UserGroupCollectionMode;
	height?: "fill" | "stretch";
};

const useUserGroupDataSource = createLocalDataSource<UserGroupOverviewModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.name, searchTerm) ||
				contains(item.description, searchTerm)
		},
		hasPagination: true
	})
	.getHook();

export const UserGroupCollection = (props: UserGroupCollectionProps): ReactElement => {
	const {
		allData,
		value,
		mode,
		searchTerm,
		height
	} = props;

	const [sortingState, setSortingState] = useState<DataSourceSortableState<UserGroupOverviewModel>>({
		propertyName: "name",
		direction: DataSourceSortDirection.Ascending
	});
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);

	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { sortingFor } = useSortHandler<UserGroupOverviewModel>(sortingState, setSortingState);
	const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm);
			setCurrentPageNumber(0);
		},
		[searchTerm]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				currentPageNumber,
				sortingState,
				searchTerm
			);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			sortingState,
			searchTerm
		]
	);

	const { currentItems, pagination } = useUserGroupDataSource(
		allData.filter((item) => value.includes(item.id)),
		{
			searchTerm,
			sorting: sortingState,
			pagination: {
				amountPerPage: 100,
				currentPageNumber
			},
		}
	);

	const deleteHandler = (id: number): void =>{
		if (mode.kind === UserAndUserGroupKind.Read) {
			throw new Error("Delete is not supported in read mode");
		}
		mode.onChange(value.filter( item => item !== id));
	};

	const deleteAllConfirmedHandler = (): void => {
		if (mode.kind === UserAndUserGroupKind.Read) {
			throw new Error("Delete all is not supported in read mode");
		}
		mode.onChange([]);
		setIsConfirmationOpen(false);
	};

	return (
		<>
			<TableView
				trackingId="user-groups-collection"
				columns={[
					mode.kind === UserAndUserGroupKind.Edit && {
						title: currentItems.length
							? (
								<IconButton
									icon="trash-can"
									kind="transparent"
									onClick={() => setIsConfirmationOpen(true)}
									trackingId="delete-button"
								/>
							)
							: "",
						width: 48,
						minWidth: 48,
						trackingId: "delete"
					},
					{
						title: localize("_Name"),
						sortable: sortingFor("name"),
						trackingId: "name"
					},
					{
						title: localize("_NumberOfUsers"),
						sortable: sortingFor("numberOfUsers"),
						trackingId: "number-of-users"
					},
					{
						title: localize("_Description"),
						sortable: sortingFor("description"),
						trackingId: "description"
					}
				]}
				noResultConfig={{
					icon: searchTerm ? "magnifying-glass" : "user-group",
					title: localize(searchTerm ? "_NoSearchResults" : "_NoGroupsYet"),
					message: localize(searchTerm ? "_NoGroupsMatchedYourSearchKeywords" : "_NoGroupsHaveBeenAdded")
				}}
				ref={tableRef}
				items={currentItems}
				itemKey="id"
				shouldEvaluateBackgroundColor
				height={height}
			>
				{
					item => (
						<Row trackingId={item.id.toString()}>
							{ mode.kind === UserAndUserGroupKind.Edit && (
								<Cell>
									<IconButton
										icon="trash-can"
										kind="transparent"
										onClick={() => deleteHandler(item.id)}
										trackingId="delete-button"
									/>
								</Cell>
							)}
							<Cell>{item.name}</Cell>
							<Cell>{item.numberOfUsers}</Cell>
							<Cell>{item.description}</Cell>
						</Row>
					)
				}
			</TableView>
			<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
			{ (isConfirmationOpen && mode.kind === UserAndUserGroupKind.Edit) && (
				<ConfirmationDialog
					title={localize("_AreYouSureYouWantToRemoveAllGroups")}
					confirmText={localize("_Remove")}
					cancelText={localize("_Cancel")}
					onCancellation={() => setIsConfirmationOpen(false)}
					onConfirmation={deleteAllConfirmedHandler}
					trackingId="delete-all-groups-confirmation-dialog"
				/>
			)}
		</>

	);
};
UserGroupCollection.displayName = "UserGroupCollection";

export const userGroupCollectionSearchPropertyKeys: readonly [LocalizationKey, ...LocalizationKey[]] = [
	"_Name",
	"_Description"
];
